/**
 * Helpers shared by the legal-styles generators (AGLC, McGill Guide, OSCOLA): reading
 * the lists and ranges people type into pinpoint boxes, and writing the numbers back in
 * each style's way.
 *
 * Nothing here is specific to one style except where a function takes the style's rule
 * as an argument; the rules themselves are cited where each style's formatter uses them.
 */

import { parsePersonName } from "@/lib/tool-kit/citation-parts";

const RE_DIGITS = /^\d+$/;
const RE_SPLIT = /\s*(?:,|;)\s*/;
const RE_RANGE = /^(\d+)\s*[-–—]\s*(\d+)$/;
const RE_RANGE_IN_TEXT = /(\d+)\s*[-–]\s*(\d+)/g;
const RE_BRACKETS = /[[\]]/g;
const RE_PARA_PREFIX = /^(?:paras?\.?|¶)\s*/i;
const RE_PAGE_PREFIX = /^(?:pp?\.?|pages?)\s*/i;
const RE_INITIAL = /^\p{Lu}\.?$/u;
const RE_INITIAL_TRAIL = /\.$/;
const RE_SPACES = /\s+/;
const RE_NEWLINES = /\r?\n+/;
const RE_AND_SPLIT = /\s+(?:and|&)\s+/i;

/** Comma- or semicolon-separated items, trimmed, empties dropped. */
export function splitItems(input: string): string[] {
  return input
    .split(RE_SPLIT)
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * A span of numbers with the second number shortened to the digits that differ. The
 * styles disagree on how short: AGLC keeps one digit ("87–8", "436–62") except that a
 * final pair between 10 and 19 stays whole ("11–14", "215–19"); OSCOLA keeps at least two.
 */
export function shortSpan(
  first: string,
  last: string,
  minimumDigits: 1 | 2,
  keepTeens: boolean
): string {
  if (
    !(RE_DIGITS.test(first) && RE_DIGITS.test(last)) ||
    first.length !== last.length ||
    Number(last) <= Number(first)
  ) {
    return `${first}–${last}`;
  }
  let shared = 0;
  while (shared < first.length - 1 && first[shared] === last[shared]) {
    shared += 1;
  }
  let keep = Math.max(last.length - shared, minimumDigits);
  const finalPair = Number(last.slice(-2));
  if (keepTeens && finalPair >= 10 && finalPair <= 19) {
    keep = Math.max(keep, 2);
  }
  return `${first}–${last.slice(-Math.min(keep, last.length))}`;
}

/** AGLC4 rule 1.10.1: "121–7", "436–62", "215–19", "11–14". */
export function aglcSpan(first: string, last: string): string {
  return shortSpan(first, last, 1, true);
}

/** Every "747-749" inside a pinpoint becomes "747–9"; other text is left as typed. */
export function shortenSpansIn(text: string): string {
  return text.replace(RE_RANGE_IN_TEXT, (_match, first: string, last: string) =>
    aglcSpan(first, last)
  );
}

/** "29", "50-59" or "[29]" → "[29]", "[50]–[59]", joined with commas. */
export function bracketedParagraphs(input: string): string {
  return splitItems(input.replace(RE_BRACKETS, ""))
    .map((item) => {
      const cleaned = item.replace(RE_PARA_PREFIX, "");
      const range = RE_RANGE.exec(cleaned);
      return range ? `[${range[1]}]–[${range[2]}]` : `[${cleaned}]`;
    })
    .join(", ");
}

/** "402" or "262-265" → "402", "262–5" under the span rule you pass in. */
export function pageList(
  input: string,
  span: (first: string, last: string) => string
): string {
  return splitItems(input)
    .map((item) => {
      const cleaned = item.replace(RE_PAGE_PREFIX, "");
      const range = RE_RANGE.exec(cleaned);
      return range ? span(range[1], range[2]) : cleaned;
    })
    .join(", ");
}

/** "H. L. A. Hart" → "HLA Hart": initials closed up, no full stops. */
export function closeUpInitials(name: string): string {
  const tokens = name.trim().split(RE_SPACES).filter(Boolean);
  const out: string[] = [];
  let run = "";
  for (const token of tokens) {
    if (RE_INITIAL.test(token)) {
      run += token.replace(RE_INITIAL_TRAIL, "");
      continue;
    }
    if (run !== "") {
      out.push(run);
      run = "";
    }
    out.push(token);
  }
  if (run !== "") {
    out.push(run);
  }
  return out.join(" ");
}

/** One author per line, or "A and B"; a name typed "Surname, Given" is turned round. */
export function readAuthorList(input: string): string[] {
  const lines = input
    .split(RE_NEWLINES)
    .flatMap((line) => (line.includes(",") ? [line] : line.split(RE_AND_SPLIT)))
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.map((line) => {
    const person = parsePersonName(line);
    if (!person || person.organization) {
      return line;
    }
    const given = person.given.join(" ");
    const name = closeUpInitials(
      given ? `${given} ${person.family}` : person.family
    );
    return person.suffix ? `${name} ${person.suffix}` : name;
  });
}

/** The surname of a name as it was written: the last word. */
export function surnameOf(name: string): string {
  const person = parsePersonName(name);
  return person ? person.family : name;
}

/** "Surname, Given Names" for a bibliography's first author. */
export function invertName(name: string): string {
  const person = parsePersonName(name);
  if (!person || person.given.length === 0) {
    return name;
  }
  return `${person.family}, ${person.given.join(" ")}`;
}
